/* eslint-disable no-shadow */

import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import {
    useGetLadderQuery,
    useGetMatchesQuery
} from 'store/modules/api/afl-api';

import { composeColumnData } from '../../utils/compose-column-data';
import { composeRowData } from '../../utils/compose-row-data';
import { modelLadderData } from '../../utils/model-ladder-data';
import { useWindowWidth } from 'common/react/hooks/use-window-resize';
import { getLeadingScore } from 'common/react/utils/get-leading-score';
import { EmptyState } from 'common/react/components/EmptyState';
import { Table } from './Table';
import { bettingOddsSelector } from 'common/store/modules/competition-nav/selectors';
import { useBettingVis } from 'common/react/hooks/use-betting-vis';
import { useGetBettingOddsQuery } from 'common/store/modules/api/cfs-api';
import { SkeletonLoader } from 'common/react/components/SkeletonLoader';

export const LadderTableFetcher = ({
    competitionId,
    compSeason,
    round,
    liveLadderData,
    displayFormTooltip
}) => {
    // eslint-disable-next-line no-magic-numbers
    const windowWidth = useWindowWidth(500); // 500 is the debounce not the viewport!!
    const isBettingOddsToggled = useSelector(bettingOddsSelector);
    const isBettingVis = useBettingVis();
    const { data: bettingData } = useGetBettingOddsQuery({
        competitionType: PULSE.app.common.match.getCompAbbr(
            compSeason.providerId
        )
    });

    const liveLadder = liveLadderData?.liveLadderPositions;
    const liveLadderCutoff = liveLadderData?.finalsCutOff || null;
    const liveLadderTopFinalsCutOff = liveLadderData?.topFinalsCutOff || null;

    // Check for a book for Premiership Winner and that the comp id matches
    const hasPremiershipWinBook = useMemo(
        () =>
            bettingData?.competition?.books.find((book) => {
                return book.name.includes('Premiership Winner');
            }) && bettingData?.competition?.seasonId === compSeason.providerId,
        [
            bettingData?.competition?.books,
            bettingData?.competition?.seasonId,
            compSeason.providerId
        ]
    );

    // Check the conditions to show betting and then either hide or show the CGM message
    useEffect(() => {
        if (
            isBettingOddsToggled.isActive &&
            isBettingVis &&
            hasPremiershipWinBook
        ) {
            PULSE.app.common.betting.cgmDisplay();
        } else {
            PULSE.app.common.betting.cgmHide();
        }
    }, [hasPremiershipWinBook, isBettingOddsToggled, isBettingVis]);

    const {
        data: ladderData,
        isFetching,
        isError
    } = useGetLadderQuery({
        compSeasonId: compSeason?.id
    });

    /**
     * Workout the winner of the comp by looking for the last match and see if
     * it is the GF (Grand Final), then return the winner's teams object
     */
    const { winner } = useGetMatchesQuery(
        {
            compSeasonId: compSeason.id,
            pageSize: 1,
            sort: 'desc'
        },
        {
            skip: !compSeason?.id,
            selectFromResult: ({ matchesData }) => {
                if (!matchesData?.length) {
                    return { winner: false };
                }

                const match = matchesData[0];

                if (match?.round?.abbreviation === 'GF') {
                    return {
                        winner: matchesData?.length
                            ? getLeadingScore(
                                  match?.home,
                                  match?.away,
                                  match?.home?.score?.totalScore,
                                  match?.away?.score?.totalScore
                              )
                            : false
                    };
                }

                return { winner: false };
            }
        }
    );

    /**
     * Get column details.
     */
    const columns = useMemo(
        () =>
            composeColumnData(
                windowWidth,
                isBettingOddsToggled?.isActive,
                isBettingVis,
                hasPremiershipWinBook
            ),
        [
            windowWidth,
            isBettingOddsToggled?.isActive,
            isBettingVis,
            hasPremiershipWinBook
        ]
    );

    // Model ladder data for each ladder entry
    const tables = useMemo(() => {
        // we want our live ladder in an array so it better matches the structure of the static data
        // ladder - makes for easier data modelling later
        let ladders = ladderData?.ladders;

        // extract only ladder from the liveLadder data

        let liveLadders = [liveLadder];

        // Obtain a non-live ladder to fill in any missing information from the live ladder.
        const staticLadder = ladderData?.ladders?.[0];

        // If there is no populated static ladder, stop here
        if (!staticLadder?.entries?.length) {
            return [];
        }

        // Override the ladders variable with the liveLadders array if we are rendering a live ladder based on the component's prop.
        if (liveLadder) {
            ladders = liveLadders;
        }

        // If we are not using liveLadders, ensure that the ladders array contains at least one ladder.
        if (!liveLadder && (!ladders || ladders.length === 0)) {
            return [];
        }

        // If we are using the liveLadders array, ensure that it contains at least one ladder.
        if ((liveLadder && !ladders) || liveLadders.length === 0) {
            return [];
        }

        // Model the data into the desired shape for our live or static ladder.
        return ladders.map((ladder) => {
            const ladderData = liveLadder
                ? modelLadderData(staticLadder, bettingData, ladder)
                : modelLadderData(ladder, bettingData);

            const premWinner = winner;
            const conferenceLabel = ladder?.conference || false;

            const finalsCutOff = liveLadderCutoff
                ? liveLadderCutoff
                : ladder?.finalsCutOff;

            const topFinalsCutOff =
                liveLadderTopFinalsCutOff || ladder?.topFinalsCutOff;

            // this util will match the column data with the correct row data
            const rowData = ladderData.map((item) =>
                composeRowData(
                    item,
                    columns,
                    finalsCutOff,
                    topFinalsCutOff,
                    conferenceLabel,
                    premWinner,
                    competitionId,
                    compSeason?.id,
                    round?.roundNumber || '',
                    PULSE.app.common.match.getCompAbbr(compSeason.providerId)
                )
            );

            return { columns, ladderData, rowData, conferenceLabel };
        });
    }, [
        bettingData,
        columns,
        compSeason?.id,
        competitionId,
        ladderData?.ladders,
        liveLadder,
        round?.roundNumber,
        winner
    ]);

    // ===== Render ============================================== //

    // Included, as may use later
    if (isFetching) {
        return <SkeletonLoader className="ladder__item-skeleton" count={1} />;
    }

    if (isError || tables.length === 0 || tables[0]?.rowData?.length === 0) {
        return (
            <div style={{ padding: '3.2rem 1.6rem' }}>
                <EmptyState cssClass="competition-nav__empty-state" />
            </div>
        );
    }

    return (
        <>
            {tables.map((table, index) => (
                <Table
                    key={index}
                    columns={table.columns}
                    data={table.rowData}
                    modifier={`ladder
                    ${
                        table.conferenceLabel
                            ? 'is-' +
                              table.conferenceLabel
                                  .toLowerCase()
                                  .replaceAll(' ', '')
                            : ''
                    }
                    ${
                        table?.ladderData[0]?.orderedBy === 'winRatio'
                            ? 'is-win-ratio'
                            : ''
                    }`}
                    cssClass="js-ladder"
                    conferenceLabel={table.conferenceLabel}
                    conferenceModifier={`${
                        table.conferenceLabel
                            ? 'is-' +
                              table.conferenceLabel
                                  .toLowerCase()
                                  .replaceAll(' ', '')
                            : ''
                    }`}
                    displayFormTooltip={displayFormTooltip}
                />
            ))}
        </>
    );
};

LadderTableFetcher.propTypes = {
    competitionId: PropTypes.string,
    compSeason: PropTypes.object,
    round: PropTypes.object,
    liveLadderData: PropTypes.any,
    displayFormTooltip: PropTypes.bool
};
